import React from "react";
import ReactDOM from "react-dom/client";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";

import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { keycloak } from "./keycloak";
import { Roles } from "./AuthProvider";
import { getCookie } from "./services/utils";
import { DASHBOARD_PATH, LOGIN_PATH } from "./constants";
import { isValidIdpAlias } from "./Sso/idpAlias";
import { reloadPageOnce } from "./reloadPage";

// An API answered HTML instead of JSON (502 page while the backend restarts).
// Reloading lets the app recover by itself once the backend is back.
window.addEventListener("unhandledrejection", (event) => {
  const error = event.reason;
  if (
    error instanceof SyntaxError &&
    error.message.includes("Unexpected token '<'")
  ) {
    if (reloadPageOnce("API returned HTML")) {
      event.preventDefault();
    }
  }
});

Sentry.init({
  dsn: "https://2dff5654162a281eb4898c90826b96b0@o4504248962973696.ingest.us.sentry.io/4509955840278528",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  beforeSend: (event) => {
    if (window.location.hostname === "host.docker.internal") {
      return null;
    }

    const clientName = getCookie("customer-name");

    event.extra = {
      ...event.extra,
      clientName: clientName || "unknown",
    };

    event.tags = {
      ...event.tags,
      client_name: clientName || "unknown",
    };

    return event;
  },
});

const COOKIE_CONFIG = {
  path: "/",
  domain: window.location.hostname,
  sameSite: "Lax" as const,
};

const TOKEN_REFRESH_INTERVAL_MS = 60000;
const MIN_TOKEN_VALIDITY_SEC = 60;

// Set by the entry points that already know which identity provider to use:
// the customer's own portal link, or their app tile in Entra / Google.
const IDP_HINT_PARAM = "idp";

/** Identity provider to skip straight to, when the URL names a usable one. */
const getIdpHint = (): string | undefined => {
  const hint = new URLSearchParams(window.location.search).get(IDP_HINT_PARAM);

  return hint !== null && isValidIdpAlias(hint) ? hint : undefined;
};

interface TokenPayload {
  customer_names?: string;
  email?: string;
  resource_access?: {
    KEYCLOAK_APP?: {
      roles?: Roles[];
    };
  };
}

const setAuthCookies = (token: string, customerName?: string) => {
  document.cookie = `access_token=${token}; ${Object.entries(COOKIE_CONFIG)
    .map(([k, v]) => `${k}=${v}`)
    .join("; ")}`;

  if (customerName) {
    document.cookie = `customer-name=${customerName}; ${Object.entries(
      COOKIE_CONFIG
    )
      .map(([k, v]) => `${k}=${v}`)
      .join("; ")}`;
  }
};

const handleTokenRefresh = () => {
  keycloak
    .updateToken(MIN_TOKEN_VALIDITY_SEC)
    .then((refreshed) => {
      // API callers read the persisted copy, not keycloak.token: without this the
      // stale token keeps being sent after expiry and every request 403s
      if (refreshed && keycloak.token) {
        localStorage.setItem("access_token", keycloak.token);
        if (keycloak.refreshToken) {
          localStorage.setItem("refresh_token", keycloak.refreshToken);
        }
        setAuthCookies(keycloak.token);
      }
    })
    .catch((error) => {
      console.error("Token refresh failed:", error);
      keycloak.login({
        redirectUri: window.location.href,
      });
    });
};

const initializeApp = async () => {
  const container = document.getElementById("root");
  if (!container) throw new Error("Root container missing");

  const root = ReactDOM.createRoot(container);

  try {
    const authenticated = await keycloak.init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: false,
    });

    if (!authenticated || !keycloak.token) {
      return keycloak.login({
        redirectUri: window.location.origin + DASHBOARD_PATH,
        idpHint: getIdpHint(),
      });
    }

    localStorage.setItem("access_token", keycloak.token);
    if (keycloak.refreshToken) {
      localStorage.setItem("refresh_token", keycloak.refreshToken);
    }

    let customerName: string | undefined;
    try {
      const decoded = jwtDecode<TokenPayload>(keycloak.token);

      if (decoded.customer_names) {
        const customerNames = decoded.customer_names.split(";");
        customerName = customerNames[0];

        if (!localStorage.getItem("selectedCustomer") && customerName) {
          localStorage.setItem("selectedCustomer", customerName);
        }
      }
    } catch (e) {
      console.warn("Token decoding error:", e);
    }

    setAuthCookies(keycloak.token, customerName);

    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );

    if (window.location.pathname === LOGIN_PATH) {
      window.location.href = DASHBOARD_PATH;
    }

    const refreshInterval = setInterval(
      handleTokenRefresh,
      TOKEN_REFRESH_INTERVAL_MS
    );

    return () => clearInterval(refreshInterval);
  } catch (error) {
    console.error("Initialization failed:", error);
    // Render error boundary or redirect to error page
    root.render(
      <div className="error-container">
        <h1>Authentication Error</h1>
        <p>Failed to initialize application. Please try again.</p>
      </div>
    );
  }
};

initializeApp();

reportWebVitals();
